import store from '@/store/store'
import { CONVERSATION_TYPES } from '@/store/constants'
import { ACTION_TYPES, INDEX_TYPES, INPUT_TYPES, STATE_ID, STATE_TYPES, OUTCOME_TYPES, GPT_VERSIONS } from '@/utils/bot-json.constants'
import clone from 'ramda/src/clone'
import head from 'ramda/src/head'
import findIndex from 'ramda/src/findIndex'
import keys from 'ramda/src/keys'
import propSatisfies from 'ramda/src/propSatisfies'
import find from 'ramda/src/find'

class ConversationModule {

  get = () => store.state.conversation

  getType = () => store.state.conversation.type

  getId = () => store.state.conversation.id

  getJsons = () => store.state.conversation.jsons

  getHashes = () => store.state.conversation.hashes

  getRootJson(type = null, id = null) {
    return this.getJsons()[type || this.getType()][id || this.getId()]
  }

  getRootJsonHash() {
    return this.getHashes()[this.getType()][this.getId()]
  }

  reset() {
    this.set(null, null,
      { answers: {}, events: {}, templates: {} },
      { answers: {}, events: {}, templates: {} }
    )
  }

  createEmptyJson(isTransitionToIdle) {
    return {
      states: {
        root: {
          type: STATE_TYPES.EMPTY,
          transitions: [{
            state: isTransitionToIdle ? STATE_ID.REF_GLOBAL_IDLE : STATE_ID.REF_GLOBAL_FIND_INTENT,
            condition: {
              input: isTransitionToIdle ? INPUT_TYPES.NONE : INPUT_TYPES.USER_INPUT,
              expression: { true: {} }
            }
          }]
        }
      },
      actions: {},
      postbacks: {}
    }
  }

  setEmptyJson(isTransitionToIdle = false) {
    this.init(this.getType(), this.getId(), this.createEmptyJson(isTransitionToIdle), this.getRootJsonHash())
  }

  set(type, id, jsons, hashes) {
    store.commit('CONVERSATION_SET', {
      type, id, jsons, hashes
    })
  }

  setId(id) {
    store.commit('CONVERSATION_ID_SET', id)
  }

  setAnswersHashes(answers) {
    store.commit('CONVERSATION_HASHES_ANSWERS_SET', answers)
  }

  setEventsHashes(events) {
    store.commit('CONVERSATION_HASHES_EVENTS_SET', events)
  }

  setTemplatesHashes(templates) {
    store.commit('CONVERSATION_HASHES_TEMPLATES_SET', templates)
  }

  setAnswers(answers) {
    store.commit('CONVERSATION_JSONS_ANSWERS_SET', answers)
  }

  setEvents(events) {
    store.commit('CONVERSATION_JSONS_EVENTS_SET', events)
  }

  setTemplates(templates) {
    store.commit('CONVERSATION_JSONS_TEMPLATES_SET', templates)
  }

  setArchivedConversationJson(archivedConversationJson) {
    store.commit('ARCHIVED_CONVERSATION_SET', clone(archivedConversationJson))
  }
  
  resetArchivedConversationJson() {
    store.commit('ARCHIVED_CONVERSATION_SET', {})
  }
  
  getArchivedConversationJson() {
    return store.state.ui.preview.archivedConversationJson
  }

  setArchivedIntentDescription(archivedIntentDescription) {
    store.commit('ARCHIVED_INTENT_DESCRIPTION_SET', archivedIntentDescription)
  }

  resetArchivedIntentDescription() {
    store.commit('ARCHIVED_INTENT_DESCRIPTION_SET', null)
  }

  getArchivedIntentDescription() {
    return store.state.ui.preview.archivedIntentDescription
  }

  addTemplate({ id, conversationJson, conversationJsonHash }) {
    store.commit('CONVERSATION_JSONS_TEMPLATES_ADD', {
      templateId: id, templateConversationJson: conversationJson
    })
    store.commit('CONVERSATION_HASHES_TEMPLATES_ADD', {
      templateId: id, templateConversationJsonHash: conversationJsonHash
    })
  }

  addPostback(postbackKey, postbackStateId, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('CONVERSATION_JSONS_POSTBACKS_ADD', {
      conversationType: type,
      conversationId: id,
      postbackKey, postbackStateId
    })
  }

  removePostback(postbackKey, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('CONVERSATION_JSONS_POSTBACKS_REMOVE', {
      conversationType: type,
      conversationId: id,
      postbackKey
    })
  }

  init(conversationType, conversationId, conversationJson, conversationJsonHash) {
    const conversationJsons = { answers: {}, events: {}, templates: {} }
    conversationJsons[conversationType] = {
      [conversationId]: conversationJson || {
        actions: {},
        postbacks: {},
        states: {}
      }
    }

    const conversationHashes = { answers: {}, events: {}, templates: {} }
    if (conversationJsonHash) {
      conversationHashes[conversationType] = {
        [conversationId]: conversationJsonHash
      }
    }

    this.set(conversationType, conversationId, conversationJsons, conversationHashes)
  }

  getState(stateId) {
    const { type, id, jsons } = this.get()
    const conversationJson = jsons[type][id]
    if(!conversationJson) {
      throw new Error('cannot find conversation json type: ' + type + ', id:' + id)
    }
    return conversationJson.states[stateId]
  }

  getTemplateState(templateRefStateId) {
    const { type, id, jsons } = this.get()
    const conversationJson = jsons[type][id]
    if(!conversationJson) {
      throw new Error('cannot find conversation json type: ' + type + ', id:' + id)
    }
    return this._getTemplateState(conversationJson, jsons.templates, templateRefStateId)

  }

  getAction(stateId, actionId) {
    const { type, id, jsons } = this.get()
    switch (type) {
      case CONVERSATION_TYPES.ANSWERS: {
        const answerJson = jsons.answers[id]
        return answerJson.actions[actionId] || this._getTemplateAction(answerJson, jsons.templates, stateId, actionId)
      }
      case CONVERSATION_TYPES.EVENTS: {
        const eventJson = jsons.events[id]
        return eventJson.actions[actionId] || this._getTemplateAction(eventJson, jsons.templates, stateId, actionId)
      }
      case CONVERSATION_TYPES.TEMPLATES:
        return jsons.templates[id].actions[actionId]
      default:
        throw new Error('invalid conversation type: ' + type)
    }
  }

  removeState(stateId, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    const state = this.getRootJson(type, id).states[stateId]
    if (!state) return

    const actions = state.actions
    if (actions) {
      actions.forEach(actionId => this.removeAction(stateId, actionId, type, id))
    }

    store.commit('STATES_REMOVE', {
      conversationType: type,
      conversationId: id,
      stateId
    })
  }

  updateRemoteAction(actionId, action) {
    store.commit('ACTIONS_ADD_REMOTE', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      actionId, action
    })
  }

  updateChatGPTAction(actionId, updatedAction) {
    const mutation = updatedAction.type === ACTION_TYPES.CHAT_GPT_CUSTOM ? 'ACTIONS_ADD_CHAT_GPT_CUSTOM' : 'ACTIONS_ADD_CHAT_GPT_GENERAL'
    store.commit(mutation, {
      conversationType: this.getType(),
      conversationId: this.getId(),
      actionId,
      action: updatedAction
    })
  }

  removeStateAction(stateId, actionId, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()
    store.commit('STATE_ACTIONS_REMOVE', {
      conversationType: type,
      conversationId: id,
      stateId, actionId
    })
  }

  removeAction(stateId, actionId, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()
    this.removeStateAction(stateId, actionId, type, id)

    store.commit('ACTIONS_REMOVE', {
      conversationType: type,
      conversationId: id,
      actionId
    })
  }

  addStateTransition(stateId, transitionStateId, condition, requestOutcome, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()
    const transition = {
      state: transitionStateId,
      condition
    }
    if (requestOutcome) {
      transition.requestOutcome = requestOutcome
    }

    store.commit('STATE_TRANSITIONS_ADD', {
      conversationType: type,
      conversationId: id,
      stateId, transition
    })
  }

  appendStateTransition(stateId, transitionStateId, condition, requestOutcome) {
    const transition = {
      state: transitionStateId,
      condition
    }
    if (requestOutcome) {
      transition.requestOutcome = requestOutcome
    }

    store.commit('STATE_TRANSITIONS_APPEND', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      stateId,
      transition
    })
  }

  addStateTemplateTransition(conversationType, conversationId, stateId, transitionStateId, condition) {
    const transition = {
      state: transitionStateId,
      condition
    }

    store.commit('STATE_TEMPLATE_TRANSITIONS_ADD', {
      conversationType, conversationId, stateId, transition
    })
  }

  appendStateTemplateTransition(stateId, transitionStateId, condition, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()
    const transition = {
      state: transitionStateId,
      condition
    }

    store.commit('STATE_TEMPLATE_TRANSITIONS_APPEND', {
      conversationType: type,
      conversationId: id,
      stateId, transition
    })
  }

  setStateTransitions(stateId, transitions, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_TRANSITIONS_SET', {
      conversationType: type,
      conversationId: id,
      stateId, transitions
    })
  }

  setStateTemplateTransitions(stateId, templateTransitions, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_TEMPLATE_TRANSITIONS_SET', {
      conversationType: type,
      conversationId: id,
      stateId, templateTransitions
    })
  }

  updateStateTransitionStateIdByIndex(stateId, transitionStateId, transitionIndex) {
    const transitions = this.getStateTransitions(stateId)
    const transition = clone(transitions[transitionIndex])
    transition.state = transitionStateId
    this.updateStateTransition(stateId, transition, transitionIndex)
  }

  updateStateTemplateTransitionStateIdByIndex(stateId, transitionStateId, transitionIndex, conversationType = null, conversationId = null) {
    const templateTransitions = this.getStateTemplateTransitions(stateId, conversationType, conversationId)
    const templateTransition = clone(templateTransitions[transitionIndex])
    templateTransition.state = transitionStateId
    this.updateStateTemplateTransition(stateId, templateTransition, transitionIndex, conversationType, conversationId)
  }

  updateStateTransition(stateId, transition, transitionIndex) {
    store.commit('STATE_TRANSITIONS_UPDATE', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      stateId, transition, transitionIndex
    })
  }

  updateStateTemplateTransition(stateId, transition, transitionIndex, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_TEMPLATE_TRANSITIONS_UPDATE', {
      conversationType: type,
      conversationId: id,
      stateId, transition, transitionIndex
    })
  }

  updateStateTemplateState(conversationType, conversationId, stateId, newTemplateState) {
    store.commit('STATE_TEMPLATE_STATE_UPDATE', {
      conversationType: conversationType,
      conversationId: conversationId,
      stateId, newTemplateState
    })
  }

  setTemplateStateAsRoot(stateId) {
    store.commit('STATE_TEMPLATE_SET_ROOT', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      stateId
    })
  }

  renameState(stateId, newStateId, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_RENAME', {
      conversationType: type,
      conversationId: id,
      stateId: stateId,
      newStateId: newStateId
    })
  }

  createTemplateRefState(conversationType, conversationId, stateId, templateId, templateStateId, templateRefSuffix, templateTransitions = [], addDefaultTransition = false, requestOutcome = null) {
    const templateRef = {
      type: STATE_TYPES.TEMPLATE_REF,
      fromTemplate: 'ref:template:' + templateId,
      templateState: templateStateId,
      postbackSuffix: templateRefSuffix,
      templateTransitions: templateTransitions,
      transitions: []
    }

    if (addDefaultTransition) {
      templateRef.transitions.push(this.createFindIntentTransition())
    }
    if (requestOutcome) {
      templateRef.requestOutcome = requestOutcome
    }

    this._createState(stateId, templateRef, conversationType, conversationId)
  }

  createTemplateState(stateId, actionId, transitions = []) {
    if (!this.actionExists(actionId)) {
      this.addOutputAction(actionId, {
        type: 'text',
        payload: ''
      })
    }

    const newState = {
      type: STATE_TYPES.OUTPUT,
      actions: [actionId],
      transitions: transitions
    }

    this._createState(stateId, newState)
  }

  createOutputState(stateId, transitions = []) {
    const newState = {
      type: STATE_TYPES.OUTPUT,
      actions: [],
      transitions: transitions
    }
    this._createState(stateId, newState)
  }

  moveActions(fromStateId, afterActionId, targetStateId) {
    const actions = this.getStateActions(fromStateId)
    const index = findIndex(actionId => actionId === afterActionId)(actions)
    const actionsToMove = actions.slice(index + 1)

    actionsToMove.forEach(action => {
      this.removeStateAction(fromStateId, action)
      this.addStateAction(targetStateId, action)
    })
  }

  createSaveContextState(stateId, contextKey, transitions) {
    const newState = {
      type: STATE_TYPES.SAVE_CONTEXT,
      contextKey: contextKey,
      transitions: transitions
    }
    this._createState(stateId, newState)
  }

  addSaveContext(stateId, nextStateId, contextStateId, contextKey) {
    const transitions = clone(this.getStateTransitions(stateId))
    this.createSaveContextState(contextStateId, contextKey, transitions)
    this.replaceLastTransitionWithExpressionTrue(stateId, contextStateId, INPUT_TYPES.USER_INPUT)
    this.replaceLastTransitionWithExpressionTrue(contextStateId, nextStateId, INPUT_TYPES.NONE)
  }

  removeSaveContext(stateId, nextStateId, contextStateId) {
    this.replaceLastTransitionWithExpressionTrue(stateId, nextStateId, INPUT_TYPES.USER_INPUT)
    this.removeState(contextStateId)
  }

  renameContextKey(stateId, newContextKey) {
    store.commit('STATE_CONTEXT_KEY_RENAME', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      stateId, newContextKey
    })
  }

  findAllTemplateRefsForState(templateStateId, templateId) {
    const templateRefs = []
    for(const type of ['answers', 'events']) {
      for(const id of keys(this.getJsons()[type])) {
        for(const stateName of keys(this.getJsons()[type][id].states)) {
          const state = this.getJsons()[type][id].states[stateName]
          if(state.fromTemplate === `ref:template:${templateId}` && state.templateState === templateStateId) {
            templateRefs.push({
              type: type,
              id: id,
              stateName: stateName,
              state: state
            })
          }
        }
      }
    }

    return templateRefs
  }

  removeTransition(stateId, transitionStateId) {
    store.commit('STATE_TRANSITIONS_REMOVE', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      stateId, transitionStateId
    })
  }

  removeTransitionIndex(stateId, transitionIndex) {
    store.commit('STATE_TRANSITIONS_REMOVE_INDEX', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      stateId, transitionIndex
    })
  }

  removeTemplateTransition(stateId, transitionStateId, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_TEMPLATE_TRANSITIONS_REMOVE', {
      conversationType: type,
      conversationId: id,
      stateId, transitionStateId
    })
  }

  removeTemplateTransitionIndex(stateId, transitionIndex, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_TEMPLATE_TRANSITIONS_REMOVE_INDEX', {
      conversationType: type,
      conversationId: id,
      stateId, transitionIndex
    })
  }

  removeLastTransition(stateId, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_TRANSITIONS_REMOVE_LAST', {
      conversationType: type,
      conversationId: id,
      stateId: stateId
    })
  }

  replaceLastTransition(stateId, transitionStateId, condition) {
    this.removeLastTransition(stateId)
    this.appendStateTransition(stateId, transitionStateId, condition)
  }

  replaceLastTransitionWithExpressionTrue(stateId, transitionStateId, input) {
    this.replaceLastTransition(stateId, transitionStateId, {
      input,
      expression: {
        true: {}
      }
    })
  }

  replaceLastTransitionWithFindIntent(stateId, conversationType = null, conversationId = null) {
    this.replaceLastTransitionWithExpressionTrue(stateId, STATE_ID.REF_GLOBAL_FIND_INTENT, INPUT_TYPES.USER_INPUT, conversationType, conversationId)
  }

  replaceLastTransitionWithIdle(stateId, conversationType = null, conversationId = null) {
    this.replaceLastTransitionWithExpressionTrue(stateId, STATE_ID.REF_GLOBAL_IDLE, INPUT_TYPES.NONE, conversationType, conversationId)
  }

  removeLastTemplateTransition(stateId, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_TEMPLATE_TRANSITIONS_REMOVE_LAST', {
      conversationType: type,
      conversationId: id,
      stateId
    })
  }

  replaceLastTemplateTransition(stateId, transitionStateId, condition, conversationType = null, conversationId = null) {
    this.removeLastTemplateTransition(stateId, conversationType, conversationId)
    this.appendStateTemplateTransition(stateId, transitionStateId, condition, conversationType, conversationId)
  }

  replaceLastTemplateTransitionWithExpressionTrue(stateId, transitionStateId, input, conversationType = null, conversationId = null) {
    this.replaceLastTemplateTransition(stateId, transitionStateId, {
      input,
      expression: {
        true: {}
      }
    }, conversationType, conversationId)
  }

  replaceLastTemplateTransitionWithFindIntent(stateId, conversationType = null, conversationId = null) {
    this.replaceLastTemplateTransitionWithExpressionTrue(stateId, STATE_ID.REF_GLOBAL_FIND_INTENT, INPUT_TYPES.USER_INPUT, conversationType, conversationId)
  }

  replaceLastTemplateTransitionWithIdle(stateId, conversationType = null, conversationId = null) {
    this.replaceLastTemplateTransitionWithExpressionTrue(stateId, STATE_ID.REF_GLOBAL_IDLE, INPUT_TYPES.NONE, conversationType, conversationId)
  }

  addOutputAction(actionId, content) {
    store.commit('ACTIONS_ADD_OUTPUT', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      actionId, content
    })
  }

  setValidationAction(actionId, validationParams) {
    store.commit('VALIDATION_SET_ACTION', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      actionId, validationParams
    })
  }

  addValidationActionToState(stateId, actionId, validationParams) {
    this.setValidationAction(actionId, validationParams)
    this.addStateAction(stateId, actionId)
  }

  addNewOutputState(stateId, actionId, transitions = [this.createFindIntentTransition()]) {
    if (!this.actionExists(actionId)) {
      this.addOutputAction(actionId, {
        type: 'text',
        payload: ''
      })
    }

    this._createState(stateId, {
      type: STATE_TYPES.OUTPUT,
      actions: [actionId],
      transitions: transitions
    })
  }

  addOutputActionToState(stateId, actionContent, actionId) {
    this.addOutputAction(actionId, actionContent)
    this.addStateAction(stateId, actionId)
  }

  addStateAction(stateId, actionId) {
    store.commit('STATE_ACTIONS_ADD', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      stateId, actionId
    })
  }

  addNewRemoteState(stateId, actionId, transitions = [], actionType = ACTION_TYPES.REMOTE) {
    if (actionType === ACTION_TYPES.REMOTE) this.addRemoteAction(actionId)

    const state = {
      type: STATE_TYPES.REMOTE,
      actions: [actionId],
      transitions: transitions
    }
    if (actionType === ACTION_TYPES.CHAT_GPT_GENERAL) {
      this.addChatGPTGeneralAction(actionId)
      state.requestOutcome = OUTCOME_TYPES.CUSTOMER_STARTED_TO_WRITE
    }

    this._createState(stateId, state)
  }

  addChatGPTGeneralAction(actionId) {
    store.commit('ACTIONS_ADD_CHAT_GPT_GENERAL', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      actionId: actionId,
      action: this.createChatGPTGeneralAction()
    })
  }

  createChatGPTGeneralAction(params = {}) {
    return {
      type: ACTION_TYPES.CHAT_GPT_GENERAL,
      deploymentId: params.deploymentId || GPT_VERSIONS.FOUR_OMNI_MINI,
      prompt: params.prompt || '',
      userPrompt: params.userPrompt || '',
      maxTokens: 4096,
      temperature: params.temperature || 0,
      topP: params.topP || 0,
      frequencyPenalty: params.frequencyPenalty || 0,
      presencePenalty: params.presencePenalty || 0,
      messageHistoryCount: params.messageHistoryCount || 0,
      allowInputFields: !!params.allowInputFields
    }
  }

  createChatGPTCustomAction(params = {}, customDataOnly) {
    return {
      type: ACTION_TYPES.CHAT_GPT_CUSTOM,
      deploymentId: params.deploymentId || GPT_VERSIONS.FOUR_OMNI_MINI,
      prompt: params.prompt || '',
      userPrompt: params.userPrompt || '',
      searchMetadata: params.searchMetadata || '',
      maxTokens: 4096,
      temperature: params.temperature || 0,
      topP: params.topP || 0,
      frequencyPenalty: params.frequencyPenalty || 0,
      presencePenalty: params.presencePenalty || 0,
      messageHistoryCount: params.messageHistoryCount || 0,
      topNDocuments: params.topNDocuments || 5,
      strictness: params.strictness || 3,
      indexId: params.indexId || '',
      indexType: params.indexType || INDEX_TYPES.SINGLE,
      customDataOnly: customDataOnly,
      dataRetrievalType: 'default',
      allowInputFields: !!params.allowInputFields
    }
  }

  addRemoteAction(actionId) {
    store.commit('ACTIONS_ADD_REMOTE', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      actionId: actionId,
      action: {
        type: 'remote',
        url: 'https://website.com/endpoint',
        httpVerb: 'POST',
        body: {},
        headers: {}
      }
    })
  }

  addConditionState(conditionStateId, conditionEndStateId) {
    const newState = {
      type: STATE_TYPES.CONDITION,
      conditionEndState: conditionEndStateId,
      transitions: [{
        state: conditionEndStateId,
        condition: {
          input: INPUT_TYPES.NONE,
          expression: { true: {} }
        }
      }]
    }
    this._createState(conditionStateId, newState)
  }

  addConditionEndState(stateId, transitions = [this.createFindIntentTransition()]) {
    this._createState(stateId, {
      type: 'conditionEnd',
      transitions: transitions
    })
  }

  addQuickReplyToAction(actionId, qrTitle, qrPayload) {
    store.commit('ACTION_QUICK_REPLIES_ADD', {
      conversationType: this.getType(),
      conversationId: this.getId(),
      actionId: actionId,
      quickReply: {
        type: 'text',
        title: qrTitle,
        payload: qrPayload
      }
    })

    const action = this.getRootJson().actions[actionId]
    const qrIndex = action.content.quick_replies.length - 1
    return qrIndex
  }

  actionExists(actionId) {
    return !!this.getRootJson().actions[actionId]
  }

  postbackExists(postbackKey) {
    return !!this.getRootJson().postbacks[postbackKey]
  }

  stateExists(stateId) {
    return !!this.getStateFromRootJson(stateId)
  }

  isStateType(stateId, type, conversationType = null, conversationId = null) {
    const state = this.getRootJson(conversationType, conversationId).states[stateId]
    return !!state && state.type === type
  }

  isConditionState(stateId, conversationType = null, conversationId = null) {
    return this.isStateType(stateId, STATE_TYPES.CONDITION, conversationType, conversationId)
  }

  isConditionEndState(stateId) {
    return this.isStateType(stateId, STATE_TYPES.CONDITION_END)
  }

  isOutputState(stateId) {
    return this.isStateType(stateId, STATE_TYPES.OUTPUT)
  }

  isRemoteState(stateId) {
    return this.isStateType(stateId, STATE_TYPES.REMOTE)
  }

  isSaveContextState(stateId) {
    return this.isStateType(stateId, STATE_TYPES.SAVE_CONTEXT)
  }

  isTemplateRefState(stateId, conversationType = null, conversationId = null) {
    return this.isStateType(stateId, STATE_TYPES.TEMPLATE_REF, conversationType, conversationId)
  }

  getStateFromRootJson(stateId) {
    return this.getRootJson().states[stateId]
  }

  getStateActions(stateId) {
    return this.getStateFromRootJson(stateId)?.actions || []
  }

  getFirstStateTransition(stateId) {
    const transitions = this.getStateTransitions(stateId)
    if (!transitions) return null

    return head(transitions)
  }

  getStateTransitions(stateId, conversationType = null, conversationId = null) {
    if(conversationType && conversationId) {
      return this.getJsons()[conversationType][conversationId].states[stateId].transitions
    }
    // a fallback for removing last bubble of conversation case
    return this.getStateFromRootJson(stateId)?.transitions
  }

  getStateTemplateTransitions(stateId, conversationType = null, conversationId = null) {
    if(conversationType && conversationId) {
      return this.getJsons()[conversationType][conversationId].states[stateId].templateTransitions
    }
    return this.getStateFromRootJson(stateId).templateTransitions
  }

  getTemplateIdByTemplateRef(stateId) {
    return this.getStateFromRootJson(stateId)?.fromTemplate?.replace('ref:template:', '')
  }

  getTemplateStateForTemplateRef(stateId) {
    return this._getTemplateState(this.getRootJson(), this.getJsons().templates, stateId)
  }

  getConditionEndStateId(conditionStateId) {
    return this.getStateFromRootJson(conditionStateId).conditionEndState
  }

  getConditionEndStateRefId(conditionEndStateId, postbackSuffix, templateId) {
    const conditionEndStateRefs = this.findAllTemplateRefsForState(conditionEndStateId, templateId)
    return conditionEndStateRefs?.find(stateRef => stateRef.state.postbackSuffix === postbackSuffix)?.stateName
  }

  isLastState(stateId) {
    const transitions = this.getStateTransitions(stateId)

    return !!find(propSatisfies((s) => ![STATE_ID.REF_GLOBAL_FIND_INTENT, STATE_ID.REF_GLOBAL_IDLE].includes(s), 'state'), transitions)
  }

  createFindIntentTransition() {
    return {
      state: STATE_ID.REF_GLOBAL_FIND_INTENT,
      condition: {
        input: INPUT_TYPES.USER_INPUT,
        expression: { true: {} }
      }
    }
  }

  _createState(stateId, state, conversationType = null, conversationId = null) {
    const type = conversationType || this.getType()
    const id = conversationId || this.getId()

    store.commit('STATE_CREATE', {
      conversationType: type,
      conversationId: id,
      stateId: stateId,
      state: state
    })
  }

  _getRootState(conversationJson, templateJsons, stateId) {
    const rootState = conversationJson.states[stateId]
    if (rootState && rootState.type !== STATE_TYPES.TEMPLATE_REF) return rootState

    return this._getTemplateState(conversationJson, templateJsons, stateId)
  }

  _getTemplateState(conversationJson, templateJsons, templateRefStateId) {
    const state = conversationJson.states[templateRefStateId]
    const templateId = state.fromTemplate.replace('ref:template:', '')
    return templateJsons[templateId].states[state.templateState]
  }

  _getTemplateAction(conversationJson, templateJsons, stateId, actionId) {
    const templateId = conversationJson.states[stateId]?.fromTemplate.replace('ref:template:', '')
    return templateJsons[templateId]?.actions[actionId]
  }
}

export default new ConversationModule()
